import axios from 'axios';
import { auth } from './firebase';
import { storage } from './storage';

const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined
  ? process.env.REACT_APP_API_URL
  : 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to attach Firebase ID token and selected curriculum
api.interceptors.request.use(
  async (config) => {
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      } catch {
        // Token fetch failed, continue without auth
      }
    }

    // Add selected curriculum header if set
    const selectedCurriculum = storage.getCurriculum();
    if (selectedCurriculum) {
      config.headers['X-Selected-Curriculum'] = selectedCurriculum;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for 401 handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // On 401, Firebase token may have expired - try to get a fresh one
    if (error.response?.status === 401 && !error.config._retry) {
      error.config._retry = true;

      const user = auth.currentUser;
      if (user) {
        try {
          // Force refresh the token
          const token = await user.getIdToken(true);
          error.config.headers.Authorization = `Bearer ${token}`;
          return api.request(error.config);
        } catch {
          // Refresh failed, redirect to login
          window.location.href = '/login';
        }
      } else {
        // No user, redirect to login
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export interface Question {
  question_id: string;
  prompt: string;
  answer_type: string;
  choices?: { label: string; value: string }[];
  hint_available: boolean;
  time_limit_seconds: number;
  difficulty_level?: number;
  learning_objective?: string;
  is_review?: boolean;
  due_date?: string;
  is_variant?: boolean;
  is_remediation?: boolean;
  target_lo?: string;
  lo_accuracy?: number;
  requires_desmos?: boolean;
  desmos_type?: 'graphing' | 'scientific' | 'four-function';
  desmos_expressions?: string[];
}

export interface SubmissionResult {
  correct: boolean;
  explanation: string;
  correct_answer?: string;
  mastery_update: {
    status: string;
    stability?: number;
    difficulty?: number;
    state?: string;
    reps?: number;
    lapses?: number;
  };
  next_review_date?: string;
  rating?: string;  // AGAIN, HARD, GOOD, EASY
  hint_text?: string;
  allow_retry?: boolean;
}

export interface SRStats {
  total_cards: number;
  new_cards: number;
  learning_cards: number;
  review_cards: number;
  relearning_cards: number;
  due_today: number;
  due_tomorrow: number;
  due_this_week: number;
  avg_stability: number | null;
  avg_difficulty: number | null;
  total_lapses: number;
  total_reviews: number;
  unseen_questions: number;
  retention_rate_30d: number | null;
}

export interface DashboardData {
  overall_progress: number;
  overall_attempted: number;
  overall_correct: number;
  units: {
    unit: number;
    name: string;
    avg_mastery: number | null;
    lessons_touched: number;
    total_lessons: number;
    attempted: number;
    correct: number;
    next_lesson: number;
  }[];
  recent_activity: {
    date: string;
    questions: number;
    correct: number;
  }[];
  streak_days: number;
  due_reviews: number;
}

export interface LessonProgress {
  lesson: number;
  total_questions: number;
  attempted: number;
  correct: number;
  accuracy: number | null;
  status: 'not_started' | 'in_progress' | 'completed';
}

export interface UnitLessonsData {
  unit: number;
  name: string;
  lessons: LessonProgress[];
  next_lesson: number;
}

export const getUnitLessons = async (unitNum: number, studentId: string): Promise<UnitLessonsData> => {
  const response = await api.get(`/api/dashboard/unit/${unitNum}/lessons?student_id=${studentId}`);
  return response.data;
};

// Questions API
export const getNextQuestion = async (
  studentId: string,
  mode: string = 'adaptive',
  unit?: number,
  lesson?: number,
  minDifficulty?: number,
  similarTo?: string,
): Promise<Question> => {
  const params = new URLSearchParams({ student_id: studentId, mode });
  if (unit) params.append('unit', String(unit));
  if (lesson) params.append('lesson', String(lesson));
  if (minDifficulty) params.append('min_difficulty', String(minDifficulty));
  if (similarTo) params.append('similar_to', similarTo);

  const response = await api.get(`/api/questions/next?${params}`);
  return response.data;
};

export const submitAnswer = async (
  studentId: string,
  questionId: string,
  answer: string,
  timeMs: number,
  hintUsed: boolean,
  attemptNumber: number = 1,
): Promise<SubmissionResult> => {
  const response = await api.post(`/api/questions/submit?student_id=${studentId}`, {
    question_id: questionId,
    given_answer: answer,
    response_time_ms: timeMs,
    hint_used: hintUsed,
    attempt_number: attemptNumber,
  });
  return response.data;
};

export const getHint = async (questionId: string, studentId: string): Promise<string> => {
  const response = await api.get(
    `/api/questions/hint?question_id=${questionId}&student_id=${studentId}`
  );
  return response.data.hint;
};

export const getSRStats = async (studentId: string): Promise<SRStats> => {
  const response = await api.get(`/api/questions/stats?student_id=${studentId}`);
  return response.data;
};

// Adaptive Learning API
export interface CalibrationInfo {
  target_difficulty: number;
  recent_accuracy: number | null;
  total_recent_attempts: number;
  avg_difficulty_attempted: number;
  struggling_los: { lo: string; description?: string; unit: number; attempts: number; correct: number; accuracy: number }[];
  mastered_los: (string | { lo: string; description?: string })[];
}

export const getCalibrationInfo = async (studentId: string, unit?: number): Promise<CalibrationInfo> => {
  const params = new URLSearchParams({ student_id: studentId });
  if (unit) params.append('unit', String(unit));
  const response = await api.get(`/api/questions/calibration?${params}`);
  return response.data;
};

export interface StrugglingLO {
  lo: string;
  description?: string;
  unit: number;
  attempts: number;
  correct: number;
  accuracy: number;
}

export const getStrugglingLOs = async (studentId: string, unit?: number): Promise<{ struggling_los: StrugglingLO[], count: number }> => {
  const params = new URLSearchParams({ student_id: studentId });
  if (unit) params.append('unit', String(unit));
  const response = await api.get(`/api/questions/struggling-los?${params}`);
  return response.data;
};

// Learning Objectives Progress API
export interface LOProgress {
  code: string;
  description: string;
  unit: number;
  attempts: number;
  correct: number;
  accuracy: number;
  status: 'mastered' | 'proficient' | 'learning' | 'struggling' | 'not_started';
}

export interface LOProgressByUnit {
  unit: number;
  name: string;
  learning_objectives: LOProgress[];
}

export interface LOProgressResponse {
  learning_objectives: LOProgress[];
  by_unit: LOProgressByUnit[];
  summary: {
    total: number;
    mastered: number;
    proficient: number;
    learning: number;
    struggling: number;
    not_started: number;
  };
}

export const getLOProgress = async (studentId: string, unit?: number): Promise<LOProgressResponse> => {
  const params = new URLSearchParams({ student_id: studentId });
  if (unit) params.append('unit', String(unit));
  const response = await api.get(`/api/dashboard/learning-objectives?${params}`);
  return response.data;
};

// Session API
export interface Session {
  id: string;
  student_id: string;
  session_type: string;
  started_at: string;
  ended_at: string | null;
  questions_attempted: number;
  questions_correct: number;
  focus_unit: number | null;
  focus_lesson: number | null;
  duration_minutes: number | null;
}

export interface SessionStats {
  total_sessions: number;
  total_time_minutes: number;
  total_questions: number;
  total_correct: number;
  avg_accuracy: number | null;
  sessions_this_week: number;
  avg_session_length: number | null;
}

export const startSession = async (
  studentId: string,
  sessionType: string = 'practice',
  focusUnit?: number,
  focusLesson?: number
): Promise<Session> => {
  const response = await api.post('/api/sessions/start', {
    student_id: studentId,
    session_type: sessionType,
    focus_unit: focusUnit,
    focus_lesson: focusLesson,
  });
  return response.data;
};

export const endSession = async (
  sessionId: string,
  questionsAttempted: number,
  questionsCorrect: number
): Promise<Session> => {
  const response = await api.post(`/api/sessions/${sessionId}/end`, {
    questions_attempted: questionsAttempted,
    questions_correct: questionsCorrect,
  });
  return response.data;
};

export const updateSession = async (
  sessionId: string,
  questionsAttempted: number,
  questionsCorrect: number
): Promise<void> => {
  await api.post(`/api/sessions/${sessionId}/update`, {
    questions_attempted: questionsAttempted,
    questions_correct: questionsCorrect,
  });
};

export const getSessionHistory = async (studentId: string, limit: number = 20): Promise<Session[]> => {
  const response = await api.get(`/api/sessions/history?student_id=${studentId}&limit=${limit}`);
  return response.data;
};

export const getSessionStats = async (studentId: string): Promise<SessionStats> => {
  const response = await api.get(`/api/sessions/stats?student_id=${studentId}`);
  return response.data;
};

// Dashboard API
export const getDashboard = async (studentId: string): Promise<DashboardData> => {
  const response = await api.get(`/api/dashboard?student_id=${studentId}`);
  return response.data;
};

export interface CurriculumConfig {
  curriculum_id: string;
  total_units: number;
  unit_names: Record<string, string>;
}

export const getCurriculumConfig = async (studentId: string): Promise<CurriculumConfig> => {
  const response = await api.get(`/api/dashboard/curriculum-config?student_id=${studentId}`);
  return response.data;
};

export interface StudentInfo {
  id: string;
  name: string;
  email: string | null;
}

export const getStudentInfo = async (studentId: string): Promise<StudentInfo> => {
  const response = await api.get(`/api/dashboard/student/${studentId}`);
  return response.data;
};

export const getMasteryOverview = async (studentId: string) => {
  const response = await api.get(`/api/dashboard/mastery?student_id=${studentId}`);
  return response.data;
};

// Mastery Gate API
export const startMasteryGate = async (studentId: string, unit: number) => {
  const response = await api.post(
    `/api/mastery-gate/start?student_id=${studentId}&unit=${unit}`
  );
  return response.data;
};

export const completeMasteryGate = async (
  sessionId: string,
  studentId: string,
  responses: { question_id: string; given_answer: string; correct: boolean; time_ms: number }[]
) => {
  const response = await api.post(
    `/api/mastery-gate/${sessionId}/complete?student_id=${studentId}`,
    { responses }
  );
  return response.data;
};

export const checkGateEligibility = async (studentId: string, unit: number) => {
  const response = await api.get(
    `/api/mastery-gate/eligibility/${studentId}?unit=${unit}`
  );
  return response.data;
};

export interface GateAttempt {
  id: string;
  gate_id: string;
  score: number;
  total: number;
  passed: boolean;
  percentage: number;
  attempted_at: string;
}

export interface GateAttemptDetails {
  attempt_id: string;
  passed: boolean;
  score: number;
  total: number;
  percentage: number;
  lo_breakdown: {
    [code: string]: {
      correct: number;
      total: number;
      description?: string;
    };
  };
  weak_areas: string[];
  question_results: {
    question_id: string;
    prompt: string;
    given_answer: string;
    correct_answer: string;
    correct: boolean;
    learning_objective: string;
  }[];
  attempted_at: string;
  unit: number;
}

export const getGateHistory = async (studentId: string, unit?: number): Promise<{ attempts: GateAttempt[] }> => {
  const url = unit
    ? `/api/mastery-gate/history/${studentId}?unit=${unit}`
    : `/api/mastery-gate/history/${studentId}`;
  const response = await api.get(url);
  return response.data;
};

export const getAttemptDetails = async (attemptId: string, studentId: string): Promise<GateAttemptDetails> => {
  const response = await api.get(
    `/api/mastery-gate/attempt/${attemptId}?student_id=${studentId}`
  );
  return response.data;
};

// Unit Progression API
export interface UnitStatus {
  unit: number;
  unlocked: boolean;
  passed_gate: boolean;
  best_score: number | null;
}

export interface ProgressionData {
  units: UnitStatus[];
  current_unit: number;
  total_units: number;
}

export const getUnitProgression = async (studentId: string): Promise<ProgressionData> => {
  const response = await api.get(`/api/mastery-gate/progression/${studentId}`);
  return response.data;
};

export const getUnitStatus = async (studentId: string, unit: number) => {
  const response = await api.get(`/api/mastery-gate/unit-status/${studentId}/${unit}`);
  return response.data;
};

// Misconception Report API
export interface MisconceptionData {
  description: string;
  count: number;
  affected_los: string[];
  affected_units: number[];
}

export interface LOMistake {
  code: string;
  unit: number;
  count: number;
  examples: {
    question_id: string;
    prompt: string;
    given_answer: string;
    correct_answer: string;
    misconceptions: string[];
  }[];
}

export interface MisconceptionReportData {
  total_mistakes: number;
  by_learning_objective: LOMistake[];
  common_misconceptions: MisconceptionData[];
  recent_mistakes: {
    question_id: string;
    learning_objective: string;
    unit: number;
    lesson: number;
    prompt: string;
    given_answer: string;
    correct_answer: string;
    explanation: string;
    date: string;
  }[];
  recommendations: string[];
}

export const getMisconceptionReport = async (studentId: string, unit?: number): Promise<MisconceptionReportData> => {
  const params = new URLSearchParams({ student_id: studentId });
  if (unit) params.append('unit', String(unit));
  const response = await api.get(`/api/dashboard/misconceptions?${params}`);
  return response.data;
};

// Time Stats API
export interface TimeStats {
  total_minutes: number;
  total_hours: number;
  total_sessions: number;
  avg_session_minutes: number;
  total_questions: number;
  total_correct: number;
  by_day_of_week: {
    day: string;
    day_index: number;
    sessions: number;
    minutes: number;
  }[];
  by_unit: {
    unit: number;
    name: string;
    sessions: number;
    minutes: number;
    questions: number;
  }[];
  current_streak_days: number;
  days_studied_last_30: number;
  recent_study_days: {
    date: string;
    minutes: number;
  }[];
}

export const getTimeStats = async (studentId: string): Promise<TimeStats> => {
  const response = await api.get(`/api/dashboard/time-stats?student_id=${studentId}`);
  return response.data;
};

// AI Features API
export interface AIConfig {
  enabled: boolean;
  model: string;
  features: {
    explain: boolean;
    worked_examples: boolean;
    hints: boolean;
    socratic: boolean;
  };
}

export interface ExplainResponse {
  explanation: string;
  question_prompt: string;
  correct_answer: string;
}

export interface WorkedExampleResponse {
  worked_example: string;
  original_question: string;
}

export interface DialogueMessage {
  role: 'student' | 'tutor';
  content: string;
}

export interface DialogueResponse {
  tutor_message: string;
  conversation_history: DialogueMessage[];
}

export const getAIUsage = async (studentId: string): Promise<{ used: number; limit: number }> => {
  const response = await api.get(`/api/ai/usage?student_id=${studentId}`);
  return response.data;
};

export const getAIConfig = async (): Promise<AIConfig> => {
  const response = await api.get('/api/ai/config');
  return response.data;
};

export const checkAIHealth = async (): Promise<{ status: string; message: string; enabled: boolean; model?: string }> => {
  const response = await api.get('/api/ai/health');
  return response.data;
};

export const getExplanation = async (
  questionId: string,
  studentId: string,
  studentAnswer?: string,
  questionPrompt?: string,
  correctAnswer?: string,
): Promise<ExplainResponse> => {
  const response = await api.post(`/api/ai/explain?student_id=${studentId}`, {
    question_id: questionId,
    student_answer: studentAnswer,
    question_prompt: questionPrompt,
    correct_answer: correctAnswer,
  });
  return response.data;
};

export const getWorkedExample = async (
  questionId: string,
  studentId: string,
  questionPrompt?: string,
  correctAnswer?: string,
): Promise<WorkedExampleResponse> => {
  const response = await api.post(`/api/ai/worked-example?student_id=${studentId}`, {
    question_id: questionId,
    question_prompt: questionPrompt,
    correct_answer: correctAnswer,
  });
  return response.data;
};

export const getAIHint = async (
  questionId: string,
  studentId: string,
  studentAnswer: string,
  previousAttempts?: string[],
  questionPrompt?: string,
  correctAnswer?: string,
): Promise<{ hint: string }> => {
  const response = await api.post(`/api/ai/hint?student_id=${studentId}`, {
    question_id: questionId,
    student_answer: studentAnswer,
    previous_attempts: previousAttempts,
    question_prompt: questionPrompt,
    correct_answer: correctAnswer,
  });
  return response.data;
};

export const sendDialogueMessage = async (
  questionId: string,
  studentId: string,
  studentMessage: string,
  conversationHistory: DialogueMessage[],
  questionPrompt?: string,
  correctAnswer?: string,
  studentAnswer?: string,
): Promise<DialogueResponse> => {
  const response = await api.post(`/api/ai/dialogue?student_id=${studentId}`, {
    question_id: questionId,
    student_message: studentMessage,
    conversation_history: conversationHistory,
    question_prompt: questionPrompt,
    correct_answer: correctAnswer,
    student_answer: studentAnswer,
  });
  return response.data;
};

// Smart Analytics API

// Gate Readiness Types
export interface GateReadinessBreakdown {
  score: number;
  weight: number;
  details: string;
}

export interface GateReadinessWeakArea {
  code: string;
  description?: string;
  accuracy: number;
  attempts: number;
}

export interface GateReadinessAction {
  primary: string;
  link: string;
}

export interface GateReadinessResponse {
  readiness_score: number;
  readiness_label: 'ready' | 'almost_ready' | 'making_progress' | 'more_practice';
  estimated_days: number | null;
  message: string;
  breakdown: {
    lo_coverage: GateReadinessBreakdown;
    recent_trend: GateReadinessBreakdown;
    response_quality: GateReadinessBreakdown;
    threshold: GateReadinessBreakdown;
  };
  weak_areas: GateReadinessWeakArea[];
  action: GateReadinessAction;
}

export const getGateReadiness = async (
  studentId: string,
  unit: number
): Promise<GateReadinessResponse> => {
  const response = await api.get(
    `/api/analytics/gate-readiness?student_id=${studentId}&unit=${unit}`
  );
  return response.data;
};

// Batch gate readiness for all units
export interface UnitGateReadiness {
  unit: number;
  readiness_score: number;
  readiness_label: 'ready' | 'almost_ready' | 'making_progress' | 'more_practice';
  message: string;
  weak_area: string | null;
}

export interface AllUnitsReadinessResponse {
  units: UnitGateReadiness[];
}

export const getAllGateReadiness = async (
  studentId: string
): Promise<AllUnitsReadinessResponse> => {
  const response = await api.get(
    `/api/analytics/gate-readiness/all?student_id=${studentId}`
  );
  return response.data;
};

// Knowledge Gaps Types
export interface KnowledgeGap {
  lo_code: string;
  description: string;
  unit: number;
  accuracy: number;
  attempts: number;
  severity: 'high' | 'medium' | 'low';
  trend: 'improving' | 'stable' | 'declining';
  error_patterns: string[];
}

export interface KnowledgeGapsResponse {
  gaps: KnowledgeGap[];
  summary: {
    high: number;
    medium: number;
    low: number;
  };
}

export const getKnowledgeGaps = async (
  studentId: string,
  unit?: number
): Promise<KnowledgeGapsResponse> => {
  const params = new URLSearchParams({ student_id: studentId });
  if (unit) params.append('unit', String(unit));
  const response = await api.get(`/api/analytics/knowledge-gaps?${params}`, {
    headers: { 'X-Selected-Child': studentId },
  });
  return response.data;
};

// Study Recommendations Types
export interface RecommendationAction {
  text: string;
  link: string;
}

export interface StudyRecommendation {
  type: 'complete_reviews' | 'struggling_lo' | 'gate_prep' | 'new_content' | 'challenge_mode';
  priority: number;
  title: string;
  description: string;
  action: RecommendationAction;
  estimated_time_minutes: number;
  metadata: Record<string, any>;
}

export interface RecommendationsResponse {
  recommendations: StudyRecommendation[];
}

export const getRecommendations = async (
  studentId: string,
  limit: number = 5
): Promise<RecommendationsResponse> => {
  const response = await api.get(
    `/api/analytics/recommendations?student_id=${studentId}&limit=${limit}`,
    { headers: { 'X-Selected-Child': studentId } }
  );
  return response.data;
};

// Weekly Summary Types
export interface WeeklySummaryData {
  period: string;
  questions_attempted: number;
  questions_correct: number;
  accuracy: number;
  days_practiced: number;
  time_minutes: number;
  streak_days: number;
  units_worked: number[];
  improved_los: { lo: string; description?: string; change: number }[];
  declined_los: { lo: string; description?: string; change: number }[];
  gates_passed: { gate: string; score: number; total: number }[];
  due_reviews: number;
}

export interface WeeklySummaryResponse {
  summary: string;
  data: WeeklySummaryData;
  generated_at: string;
  cached: boolean;
}

export const getWeeklySummary = async (
  studentId: string,
  forceRefresh: boolean = false
): Promise<WeeklySummaryResponse> => {
  const response = await api.post(
    `/api/analytics/weekly-summary?student_id=${studentId}`,
    { force_refresh: forceRefresh }
  );
  return response.data;
};

export interface SummaryStatusResponse {
  has_cached: boolean;
  cache_age_hours: number | null;
  can_refresh: boolean;
  generated_at: string | null;
}

export const getSummaryStatus = async (studentId: string): Promise<SummaryStatusResponse> => {
  const response = await api.get(`/api/analytics/summary-status?student_id=${studentId}`);
  return response.data;
};

// Goal Progress (student-facing)
export interface GoalProgressData {
  goals: {
    questions_per_day: number | null;
    minutes_per_day: number | null;
    accuracy_target: number | null;
  } | null;
  progress: {
    questions_today: number;
    minutes_today: number;
    accuracy_today: number;
  } | null;
  met: {
    questions_met: boolean;
    minutes_met: boolean;
    accuracy_met: boolean;
  } | null;
}

export const getGoalProgress = async (studentId: string): Promise<GoalProgressData> => {
  const response = await api.get(`/api/dashboard/goal-progress?student_id=${studentId}`);
  return response.data;
};

// Assignments (student-facing)
export interface Assignment {
  id: string;
  student_id: string;
  created_by: string;
  title: string;
  target_unit: number;
  target_lesson: number | null;
  num_questions: number;
  due_date: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'expired';
  questions_completed: number;
  questions_correct: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export const getMyAssignments = async (studentId: string): Promise<Assignment[]> => {
  const response = await api.get(`/api/assignments?student_id=${studentId}`);
  return response.data;
};

export const startAssignment = async (assignmentId: string, studentId: string): Promise<any> => {
  const response = await api.post(`/api/assignments/${assignmentId}/start?student_id=${studentId}`);
  return response.data;
};

export const updateAssignmentProgress = async (
  assignmentId: string,
  studentId: string,
  questionsCompleted: number,
  questionsCorrect: number
): Promise<any> => {
  const response = await api.post(
    `/api/assignments/${assignmentId}/progress?student_id=${studentId}&questions_completed=${questionsCompleted}&questions_correct=${questionsCorrect}`
  );
  return response.data;
};

// Feedback API
export type FeedbackType = 'unclear' | 'answer_wrong' | 'technical' | 'other';

export interface FeedbackSubmit {
  question_id: string;
  feedback_type: FeedbackType;
  description?: string;
}

export interface FeedbackResponse {
  id: string;
  question_id: string;
  student_id: string;
  feedback_type: string;
  description: string | null;
  status: string;
  created_at: string;
}

export const submitFeedback = async (
  studentId: string,
  feedback: FeedbackSubmit
): Promise<FeedbackResponse> => {
  const response = await api.post(`/api/feedback/?student_id=${studentId}`, feedback);
  return response.data;
};

export default api;
